<script lang="ts" setup>
import { QBtn } from 'quasar'
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import { usePrevioDS } from '../../composables/helpers'
import { PBadgeColor } from '../badge/types'
import PBadge from '../badge/PBadge.vue'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextCta from '../typography/links/PTextCta.vue'

type Props = {
  text?: null | string
  color?: Color.PRIMARY | Color.INFO | Color.WHITE
  iconStart?: string
  iconEnd?: string
  badgeText?: string | number | null
  hideTextOnMobile?: boolean
  disable?: boolean
  cy: string
}

type Emits = {
  (e: 'click', v: Event): void
}

const props = withDefaults(defineProps<Props>(), {
  text: null,
  color: Color.PRIMARY,
  disable: false,
})

const emits = defineEmits<Emits>()

const ds = usePrevioDS()

const bgColor = computed<
  | Color.PRIMARY_TRANSPARENT
  | Color.INFO_TRANSPARENT
  | Color.WHITE_TRANSPARENT
  | Color.GREY_200
  | Color.GREY_600
>(() => {
  switch (props.color) {
    case Color.PRIMARY:
      return !props.disable ? Color.PRIMARY_TRANSPARENT : Color.GREY_200
    case Color.INFO:
      return !props.disable ? Color.INFO_TRANSPARENT : Color.GREY_200
    case Color.WHITE:
      return !props.disable ? Color.WHITE_TRANSPARENT : Color.GREY_600

    default:
      throw 'Invalid prop of color in PBtnBadge'
  }
})

const textColor = computed<Color.WHITE | Color.GREY_500 | Color.GREY_600>(() => {
  switch (props.color) {
    case Color.WHITE:
      return !props.disable ? Color.WHITE : Color.GREY_500

    default:
      return Color.GREY_600
  }
})

const iconColor = computed<
  Color.PRIMARY | Color.INFO | Color.WHITE | Color.GREY_600 | Color.GREY_500
>(() => {
  switch (props.color) {
    case Color.WHITE:
      return !props.disable ? Color.WHITE : Color.GREY_500

    default:
      return !props.disable ? props.color : Color.GREY_600
  }
})

const badgeColor = computed<PBadgeColor.WARNING | PBadgeColor.NEGATIVE>(() => {
  switch (props.color) {
    case Color.WHITE:
      return PBadgeColor.WARNING

    default:
      return PBadgeColor.NEGATIVE
  }
})

const showText = computed<boolean>(() => {
  return !(props.hideTextOnMobile && ds.screen().isMobile) && !!props.text
})
</script>

<template>
  <q-btn
    class="ds-btn-badge"
    unelevated
    no-caps
    :color="bgColor"
    :text-color="textColor"
    :disable="disable"
    :data-cy="cy"
    @click="(e: Event) => emits('click', e)"
  >
    <template v-if="iconStart">
      <p-icon :icon="iconStart" :color="iconColor" />
    </template>
    <template v-if="showText">
      <p-space :mobile="PSpaceSize.XS" />
      <p-text-cta :text="text" />
      <p-space :mobile="PSpaceSize.XS" />
    </template>
    <template v-if="iconEnd">
      <p-icon :icon="iconEnd" :color="iconColor" />
    </template>
    <slot />
    <p-badge v-if="badgeText" :text="badgeText" :color="badgeColor" floating />
  </q-btn>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';

.ds-btn-badge {
  @include mobile {
    padding: 10px;
  }

  @include laptopAndLarge {
    padding: 4px 6px 4px 6px;
  }

  :deep(.q-badge--floating) {
    top: -4px;
    right: -2px;
  }
}
</style>
